import { BrowserRouter, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { getHeaderOffsetClasses } from "./lib/layout/header-offset";
import { useTopAnnouncementVisible } from "./hooks/use-top-announcement-visible";
import { ThemeProvider } from "./contexts/ThemeContext";
import { NotificationBadgeProvider } from "./contexts/NotificationBadgeContext";
import { AdminNotificationBadgeProvider } from "./contexts/AdminNotificationBadgeContext";
import { AvailabilityProvider } from "./contexts/AvailabilityContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import MobileTabBar from "./components/navigation/MobileTabBar";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import CookieConsent from "./components/CookieConsent";
import FloatingChatbot from "./components/FloatingChatbot";
import ErrorBoundary from "./components/ErrorBoundary";
import { QueryClientProvider } from "@tanstack/react-query";
import { QueryClient } from "@tanstack/query-core";
import AppRoutes from "./routes";
import SupabaseProvider from "./lib/supabase/supabase-provider";
import SEOOrganizationSchema from "./components/seo/SEOOrganizationSchema";
import { runImageDiagnostics } from "./lib/diagnostics/imageDiagnostics";
import { trackPageView as trackMetaPixelPageView, isMetaPixelReady } from "./lib/services/meta-pixel.service";
import { trackPageView as trackGAPageView, isGAReady, trackWhatsAppClick } from "./lib/services/google-analytics.service";
import { loadGA, isGALoaded } from "./lib/ga/loadGA";
import { trackPageView as trackPostHogPageView, isPostHogReady } from "./lib/services/posthog.service";
import { getWhatsAppLink } from "./lib/config/company";
import { cn } from "./lib/utils";
import { useAppPrompt } from "@/hooks/useAppPrompt";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes - cache time (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry once on failure
      refetchOnMount: true, // Refetch when component mounts
    },
  },
});

const AppInstallModal = lazy(() => import("@/components/AppInstallModal"));

function AppContent() {
  const location = useLocation();
  const { user } = useAuth();
  const topAnnouncementVisible = useTopAnnouncementVisible();
  const { isModalOpen, isOpeningApp, handleOpenApp, handleContinueOnWeb } = useAppPrompt();

  // Vérifier si on est sur une page d'authentification
  const isAuthPage = location.pathname.startsWith("/auth");
  const isMagicLinkPage =
    location.pathname === "/dispatch" || location.pathname.startsWith("/agency/fleet");

  // Vérifier si on est sur une page admin (ne pas afficher le Navbar principal)
  const isAdminPage = location.pathname.startsWith("/admin");

  // Vérifier si on est sur la page d'accueil (HeroPremium doit commencer directement sous la Navbar)
  const isHomePage = location.pathname === "/";

  // Recherche : barre sticky gère l'offset (évite double padding sous la navbar)
  const isSearchPage = location.pathname === "/search";

  const isSEOPage = location.pathname.startsWith("/fr/") || location.pathname.startsWith("/en/")
    || location.pathname.startsWith("/faq/")
    || location.pathname.startsWith("/blog/")
    || location.pathname.startsWith("/guide/")
    || location.pathname.startsWith("/compare/")
    || location.pathname.startsWith("/best-")
    || location.pathname.startsWith("/top-")
    || location.pathname === "/about-rakb"
    || location.pathname === "/a-propos-rakb"
    || location.pathname === "/contact-rakb";
  const showFooter = isHomePage || isSEOPage;

  // Page détail véhicule : barre sticky en bas (mobile) — placer les boutons flottants au-dessus
  const isCarDetailPage = /^\/cars\/[^/]+$/.test(location.pathname);

  // Page réservation : max 2 floating (chat + scroll), masquer WhatsApp, placer au-dessus du sticky
  const isReservationPage = /^\/cars\/[^/]+\/reserve$/.test(location.pathname);

  // Floating buttons: clear of sticky bars/footers (default bottom-24). On car detail/reserve use higher offset so chat/scroll sit above sticky on mobile web (bottom-32) and desktop (sm:bottom-28). Same right margin (right-4 sm:right-8), same size (h-12 w-12), gap-3.
  const floatingBottom = isCarDetailPage || isReservationPage ? "bottom-32 sm:bottom-28" : "bottom-24 sm:bottom-24";
  const whatsappBottom = isCarDetailPage
    ? "bottom-32 sm:bottom-28"
    : isSearchPage
      ? "bottom-40 sm:bottom-36"
      : isHomePage
        ? "bottom-3 sm:bottom-4"
        : "bottom-14 sm:bottom-16";
  const floatingHorizontal = "right-4 sm:right-8";
  const floatingHorizontalLeft = "left-4 sm:left-8";

  // Exécuter les diagnostics d'images au démarrage (dev seulement)
  useEffect(() => {
    if (import.meta.env.DEV) {
      runImageDiagnostics();
    }
  }, []);

  // Référence pour éviter le double tracking au premier rendu
  const isFirstRender = useRef(true);
  const initialPageViewTracked = useRef(false);

  // Tracker la page initiale si GA est déjà prêt (consent donné précédemment)
  useEffect(() => {
    // Vérifier si le consentement analytics a déjà été donné
    const consent = localStorage.getItem('rakb_cookie_consent');
    const savedPreferences = localStorage.getItem('rakb_cookie_preferences');
    
    if (consent === 'accepted' && savedPreferences) {
      try {
        const prefs = JSON.parse(savedPreferences);
        if (prefs.analytics && !initialPageViewTracked.current) {
          console.log('[App] 🍪 Consentement analytics détecté, chargement Google Analytics...');
          // Si analytics est accepté, charger GA et tracker la page initiale
          if (!isGALoaded()) {
            loadGA()
              .then(() => {
                console.log('[App] ✅ GA chargé (consent préalable)');
                
                // Attendre que GA soit prêt avant de tracker
                setTimeout(() => {
                  if (isGAReady()) {
                    trackGAPageView();
                    initialPageViewTracked.current = true;
                    console.log('[App] 📊 PageView initial tracké (consent préalable)');
                  } else {
                    console.warn('[App] ⚠️ GA pas encore prêt après chargement');
                  }
                }, 300);
              })
              .catch((error) => {
                console.error('[App] ❌ Erreur chargement GA (consent préalable):', error);
              });
          } else {
            // GA déjà chargé, vérifier qu'il est prêt avant de tracker
            const tryTrackPageView = (attempts = 0) => {
              const maxAttempts = 10; // 1 second max (10 * 100ms)
              
              if (isGAReady()) {
                trackGAPageView();
                initialPageViewTracked.current = true;
                console.log('[App] 📊 PageView initial tracké (GA déjà chargé)');
              } else if (attempts < maxAttempts) {
                // Retry after 100ms
                setTimeout(() => tryTrackPageView(attempts + 1), 100);
              } else {
                console.warn('[App] ⚠️ GA pas encore prêt après plusieurs tentatives');
              }
            };
            
            // Start trying after a short delay
            setTimeout(() => tryTrackPageView(), 200);
          }
        }
      } catch (e) {
        // Ignore parsing errors
        if (import.meta.env.DEV) {
          console.warn('[App] Erreur parsing préférences cookies:', e);
        }
      }
    }
  }, []);

    // Tracker les changements de page pour Meta Pixel, Google Analytics et PostHog (SPA navigation)
  useEffect(() => {
    // Skip le premier rendu pour éviter double tracking avec la page initiale
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Tracker le PageView pour Meta Pixel si initialisé
    if (isMetaPixelReady()) {
      trackMetaPixelPageView();
      console.debug('[App] PageView Meta Pixel tracké:', location.pathname);
    }

    // Tracker le PageView pour Google Analytics si initialisé
    if (isGAReady()) {
      trackGAPageView();
      console.debug('[App] PageView GA tracké:', location.pathname);
    }

    // Tracker le PageView pour PostHog si initialisé
    if (isPostHogReady()) {
      trackPostHogPageView();
      console.debug('[App] PageView PostHog tracké:', location.pathname);
    }
  }, [location.pathname]);

  // Padding-top = hauteur réelle du header (invité vs connecté), aligné Navbar.tsx
  const paddingTop =
    isHomePage || isSearchPage || isMagicLinkPage
      ? ""
      : getHeaderOffsetClasses(Boolean(user), topAnnouncementVisible);

  return (
    <>
      {/* Skip links pour navigation clavier */}
      <div className="sr-only focus-within:not-sr-only focus-within:absolute focus-within:z-[100] focus-within:top-4 focus-within:left-4">
        <a
          href="#main-content"
          className="block px-4 py-2 bg-green-night text-white rounded-lg font-semibold shadow-lg focus:outline-none focus:ring-2 focus:ring-green-light focus:ring-offset-2"
        >
          Aller au contenu principal
        </a>
      </div>
      <SEOOrganizationSchema />
      <ScrollToTop />
      <Toaster />
      <SonnerToaster />
      <CookieConsent />
      {/* Stack fixe bas-droite : chat en bas, scroll-to-top au-dessus. Slot rendu avant FloatingChatbot pour que le portal ait la cible au mount. */}
      {!isAuthPage && !isMagicLinkPage && (
        <div
          className={cn(
            "fixed z-[100] flex flex-col-reverse items-end gap-3 pl-4 pr-4 sm:pl-8 sm:pr-8",
            floatingHorizontal,
            floatingBottom
          )}
          aria-label="Actions rapides"
        >
          <div id="floating-chat-button-slot" className="contents" />
          <ScrollToTopButton />
        </div>
      )}
      {/* FloatingChatbot disabled temporarily */}
      {/* {!isAuthPage && <FloatingChatbot />} */}
      {/* WhatsApp fixe à gauche — masqué pendant le flow réservation (max 2 floating: chat + scroll) */}
      {!isAuthPage && !isMagicLinkPage && !isReservationPage && (
        <a
          href={getWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWhatsAppClick()}
          className={cn(
            "fixed z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-soft hover:shadow-medium hover:bg-[#20BD5A] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 touch-manipulation active:scale-[0.97] dark:ring-offset-gray-800",
            floatingHorizontalLeft,
            whatsappBottom
          )}
          aria-label="Contacter RAKB sur WhatsApp"
        >
          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
      )}
      <div
        className={`${isAuthPage ? "fixed inset-0" : "min-h-screen"} bg-background flex flex-col`}
      >
        {!isAuthPage && !isMagicLinkPage && <Navbar />}
        <div className={`flex-1 ${!isAuthPage && !isMagicLinkPage ? paddingTop : ""} transition-all duration-300 bg-background`}>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </div>
        {!isAuthPage && showFooter && <Footer />}
        {!isAuthPage && !isAdminPage && !isMagicLinkPage && <MobileTabBar />}
      </div>
      <Suspense fallback={null}>
        <AppInstallModal
          isOpen={isModalOpen}
          isLoading={isOpeningApp}
          onOpenApp={handleOpenApp}
          onContinueOnWeb={handleContinueOnWeb}
        />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <SupabaseProvider>
            <AuthProvider>
              <AvailabilityProvider>
                <NotificationBadgeProvider>
                  <AdminNotificationBadgeProvider>
                    <CurrencyProvider>
                      <BrowserRouter>
                        <AppContent />
                      </BrowserRouter>
                    </CurrencyProvider>
                  </AdminNotificationBadgeProvider>
                </NotificationBadgeProvider>
              </AvailabilityProvider>
            </AuthProvider>
          </SupabaseProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
